import { useEffect, useMemo, useRef, useState } from 'react';

import $query from 'app/stores/query/$query';
import $metrics from 'app/stores/metrics/$metrics';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import { calculateSampleRate } from '@kentik/ui-shared/util/sampleRate';
import { FlowMeasurement, getFlowQueries } from '../deviceFlowMetricsQueryUtils';

export const getAvailableReceivedMeasurements = ({ skipKproxy = false }) => {
  const measurements = [FlowMeasurement.FLOW];

  if ($metrics.measurementModelByName(FlowMeasurement.FLOW_PROXY)) {
    measurements.push(FlowMeasurement.FLOW_PROXY);
  }

  if (!skipKproxy && $metrics.measurementModelByName(FlowMeasurement.KPROXY)) {
    measurements.push(FlowMeasurement.KPROXY);
  }

  return measurements;
};

/**
 * @typedef {Object} Filter
 * @property {string} filterField
 * @property {string} operator
 * @property {string|number} filterValue
 */

/**
 * @typedef {Object} PlanFlowRateResult
 * @property {number} avgReceivedFps
 * @property {number} avgStoredFps
 * @property {number} flowSampleRate
 * @property {boolean} hasFlowData
 * @property {boolean} loadingPlanFlow
 * @property {boolean} planFlowError
 * @property {Array<[number, number]>} [combinedReceivedFlowTimeseries]
 * @property {Object<string, object|null>} [receivedFlowResultsByMeasurement]
 * @property {object|null} [storedFlowData]
 * @property {Array<[number, number]>} [storedFlowTimeseries]
 */

/**
 * Hook to fetch and calculate plan flow rate metrics
 *
 * Handles fetching received and stored flow data for a plan across multiple flow measurement types
 * (FLOW, FLOW_PROXY, and KPROXY). Combines received flow data from all sources and calculates
 * flow sample rates. Automatically skips data fetching until plan ID is available.
 *
 * @param {Object} options - Configuration object
 * @param {string[]} [options.dimensions=['plan_id']] - Dimensions to group flow data by
 * @param {Filter[]} [options.filters] - Query filters; if not provided, plan ID filter is auto-generated
 * @param {number} [options.limit=1000] - Maximum number of results to return
 * @param {string} [options.lookback='PT86400S'] - Time lookback period for flow queries (ISO 8601 duration)
 * @param {object|null} [options.merge=null] - Optional merge condition for kmetrics queries
 * @param {string|number} options.planId - The plan ID to fetch flow data for
 * @param {string} [options.rollup='p95'] - Rollup function to apply (p95, avg, max, etc.)
 * @param {boolean} [options.skipKproxy=false] - If true, skips fetching flow data from KPROXY measurement
 * @param {boolean} [options.withRawData=false] - If true, includes raw flow data and timeseries in returned object
 *
 * @returns {PlanFlowRateResult} Flow metrics and status object
 */
function usePlanFlowRate({
  dimensions = ['plan_id'],
  filters,
  limit = 1000,
  lookback = 'PT86400S',
  merge = null,
  planId,
  rollup = 'p95',
  withRawData = false,
  skipKproxy = false
}) {
  const [receivedFlowResultsByMeasurement, setReceivedFlowResultsByMeasurement] = useState({});
  const [storedFlowData, setStoredFlowData] = useState(null);
  const [loadingPlanFlow, setLoadingPlanFlow] = useState(false);
  const [planFlowError, setPlanFlowError] = useState(false);

  // Use ref outside of state to track fetch state and prevent unnecessary re-fetches
  const isFetchingRef = useRef(false);

  if (!filters) {
    filters = planId
      ? [
          {
            filterField: 'plan_id',
            operator: '=',
            filterValue: planId
          }
        ]
      : [];
  }

  useEffect(() => {
    // Skip if plan hasn't loaded yet
    if (!planId || isFetchingRef.current) {
      const noopCleanup = () => {};
      return noopCleanup;
    }

    isFetchingRef.current = true;
    let isCanceled = false;
    let flowRequest;

    const availableReceivedMeasurements = getAvailableReceivedMeasurements({ skipKproxy });

    // Build query for received (inbound) flow metrics
    const receivedFlowQueries = availableReceivedMeasurements
      .map((measurement) =>
        getFlowQueries({
          dimensions,
          filters,
          limit,
          lookback,
          metric: 'in',
          merge,
          rollup,
          measurement
        })
      )
      .filter(Boolean);

    // Build query for stored (outbound) flow metrics; all devices use FLOW measurement for stored flow
    const storedFlowQuery = getFlowQueries({
      dimensions,
      filters,
      limit,
      lookback,
      metric: 'out',
      merge,
      rollup,
      measurement: FlowMeasurement.FLOW
    });

    const fetchFlowData = async () => {
      setLoadingPlanFlow(true);

      flowRequest = makeCancelable(
        Promise.all([...receivedFlowQueries.map((query) => $query.runQuery(query)), $query.runQuery(storedFlowQuery)])
      );

      try {
        const results = await flowRequest.promise;
        const storedFlowResults = results[results.length - 1];
        const receivedResults = results.slice(0, -1);

        const resultsByMeasurement = availableReceivedMeasurements.reduce((acc, measurement, index) => {
          acc[measurement] = receivedResults[index]?.results;
          return acc;
        }, {});

        const kproxyDeviceMap = {};
        if (resultsByMeasurement[FlowMeasurement.KPROXY]) {
          resultsByMeasurement[FlowMeasurement.KPROXY].models.reduce((acc, result) => {
            const did = result.get('did');
            if (did) {
              acc[did] = result;
            }
            return acc;
          }, kproxyDeviceMap);

          resultsByMeasurement[FlowMeasurement.FLOW] = {
            ...resultsByMeasurement[FlowMeasurement.FLOW],
            models: resultsByMeasurement[FlowMeasurement.FLOW].models.filter((result) => {
              const did = result.get('did');
              return !did || !kproxyDeviceMap[did];
            })
          };
        }

        if (!isCanceled) {
          setPlanFlowError(false);
          setReceivedFlowResultsByMeasurement(resultsByMeasurement);
          setStoredFlowData(storedFlowResults?.results);
        }
      } catch (error) {
        if (error instanceof CanceledError) {
          isCanceled = true;
          return;
        }

        // Clear data states on error to prevent stale data
        if (!isCanceled) {
          setReceivedFlowResultsByMeasurement({});
          setStoredFlowData(null);
          setPlanFlowError(true);
        }
        console.error('Error fetching plan flow data: ', error);
      } finally {
        if (!isCanceled) {
          setLoadingPlanFlow(false);
        }
        isFetchingRef.current = false;
      }
    };

    fetchFlowData();

    return () => {
      isCanceled = true;
      if (flowRequest) {
        flowRequest.cancel();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, lookback]);

  const receivedSources = Object.values(receivedFlowResultsByMeasurement || {}).filter(Boolean);

  const hasFlowData = useMemo(() => {
    if (!receivedSources.length && !storedFlowData) {
      return false;
    }

    const hasReceivedData = receivedSources.some(
      (source) => source?.size > 0 && (source.models?.[0]?.get('timeseries') || []).length > 0
    );
    const hasStoredData = !!(
      storedFlowData &&
      storedFlowData.size > 0 &&
      (storedFlowData.models[0].get('timeseries') || []).length > 0
    );

    return hasReceivedData || hasStoredData;

    // eslint will complain about missing receivedSources dep,but it's derived from receivedFlowResultsByMeasurement,
    // which is the actual state variable that changes, so we disable the warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedFlowResultsByMeasurement, storedFlowData]);

  const avgReceivedFps = receivedSources.reduce(
    (sum, source) => sum + (source?.models[0]?.get(`${rollup}_in`) || 0),
    0
  );

  const avgStoredFps = storedFlowData?.models[0]?.get(`${rollup}_out`) || 0;

  const flowSampleRate = calculateSampleRate(avgReceivedFps, avgStoredFps);

  const combinedReceivedFlowTimeseries = useMemo(() => {
    const storedFlowTimeseries = storedFlowData?.models[0]?.get('timeseries');
    if (!storedFlowTimeseries || !storedFlowTimeseries.length) {
      return [];
    }

    const receivedData = [];

    const sourcesWithData = receivedSources.filter((source) => !!source?.models[0]?.get('timeseries')?.length);

    storedFlowTimeseries.forEach((tuple, index) => {
      const combinedFlow = sourcesWithData.reduce((sum, source) => {
        const timeseries = source.models[0].get('timeseries');
        return timeseries[index] ? sum + timeseries[index][1] : sum;
      }, 0);
      receivedData.push([tuple[0], combinedFlow]);
    });

    return receivedData;

    // eslint will complain about missing receivedSources dep,but it's derived from receivedFlowResultsByMeasurement,
    // which is the actual state variable that changes, so we disable the warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedFlowResultsByMeasurement, storedFlowData]);

  // Return basic metrics by default, or include raw data if requested
  const basicReturn = {
    avgReceivedFps,
    avgStoredFps,
    planFlowError,
    flowSampleRate,
    hasFlowData,
    loadingPlanFlow
  };

  return withRawData
    ? {
        ...basicReturn,
        combinedReceivedFlowTimeseries,
        receivedFlowResultsByMeasurement,
        storedFlowData,
        storedFlowTimeseries: storedFlowData?.models[0]?.get('timeseries') || []
      }
    : basicReturn;
}

export default usePlanFlowRate;
